import { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { apiErrorMessage } from '@shared/api/client';
import { useUpdateRequiredDocuments } from '@shared/hooks/use-transaction-actions';
import type { Uuid } from '@shared/types/api';

const PRESET_DOCUMENTS = ['Aadhar', 'PAN Card', 'Licence', 'Passport'];

export type RequiredDocumentsModalProps = {
  open: boolean;
  onClose: () => void;
  transactionId: Uuid;
  currentDocs: string[];
};

export function RequiredDocumentsModal({
  open,
  onClose,
  transactionId,
  currentDocs = [],
}: RequiredDocumentsModalProps) {
  const updateDocs = useUpdateRequiredDocuments(transactionId);
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [customDocInput, setCustomDocInput] = useState('');
  const [customList, setCustomList] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
      setCustomDocInput('');
      const normalizedCurrent = currentDocs.map((d) => d.trim()).filter(Boolean);
      setSelectedDocs(normalizedCurrent);

      // Find non-preset custom documents
      const customItems = normalizedCurrent.filter(
        (doc) => !PRESET_DOCUMENTS.some((preset) => preset.toLowerCase() === doc.toLowerCase()),
      );
      setCustomList(customItems);
    }
  }, [open, currentDocs]);

  // Combine default presets and custom items in order
  const allDocOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of PRESET_DOCUMENTS) set.add(p);
    for (const c of customList) set.add(c);
    for (const d of selectedDocs) set.add(d);
    return Array.from(set);
  }, [customList, selectedDocs]);

  function handleToggleDoc(docName: string) {
    setSelectedDocs((prev) => {
      const exists = prev.some((d) => d.toLowerCase() === docName.toLowerCase());
      if (exists) {
        return prev.filter((d) => d.toLowerCase() !== docName.toLowerCase());
      } else {
        return [...prev, docName];
      }
    });
  }

  function handleAddCustomDoc() {
    const trimmed = customDocInput.trim();
    if (!trimmed) return;

    const existsInOptions = allDocOptions.some((d) => d.toLowerCase() === trimmed.toLowerCase());

    if (!existsInOptions) {
      setCustomList((prev) => [...prev, trimmed]);
    }

    const isSelected = selectedDocs.some((d) => d.toLowerCase() === trimmed.toLowerCase());
    if (!isSelected) {
      setSelectedDocs((prev) => [...prev, trimmed]);
    }

    setCustomDocInput('');
  }

  async function handleSave() {
    setError(null);
    try {
      await updateDocs.mutateAsync(selectedDocs);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to update required documents'));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit Required Documents Checklist"
      footer={
        <div className="flex items-center justify-end gap-2 w-full sm:w-auto">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={updateDocs.isPending}
            className="text-xs h-9 px-4"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={updateDocs.isPending}
            className="text-xs h-9 px-4 bg-brand-700 hover:bg-brand-800 text-white font-medium shadow-sm"
          >
            {updateDocs.isPending ? 'Saving...' : 'Save Checklist'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 py-1">
        <p className="text-xs text-gray-600 leading-relaxed">
          Select or uncheck the documents required for this service. You can also add custom
          document requirements.
        </p>

        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">
            {error}
          </div>
        ) : null}

        <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4 space-y-3">
          <label className="block text-xs font-semibold text-gray-800 uppercase tracking-wide">
            Required Documents *
          </label>

          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-2">
            {allDocOptions.map((doc) => {
              const checked = selectedDocs.some((d) => d.toLowerCase() === doc.toLowerCase());
              return (
                <label
                  key={doc}
                  className={`flex items-center gap-2.5 rounded-md border px-3 py-2 text-xs font-medium cursor-pointer transition-colors ${
                    checked
                      ? 'border-brand-300 bg-brand-50/70 text-brand-950 font-semibold shadow-2xs'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleToggleDoc(doc)}
                    className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                  />
                  <span className="truncate">{doc}</span>
                </label>
              );
            })}
          </div>

          <div className="pt-2 border-t border-gray-200/80">
            <label className="block text-xs font-medium text-gray-700 mb-1.5">
              + Others / Custom Document
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={customDocInput}
                onChange={(e) => setCustomDocInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCustomDoc();
                  }
                }}
                placeholder="e.g. Bank Book, Driving License..."
                className="flex-1 h-9 px-3 text-xs rounded-md border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 bg-white"
              />
              <button
                type="button"
                onClick={handleAddCustomDoc}
                className="h-9 px-3 text-xs font-medium bg-gray-800 text-white rounded-md hover:bg-gray-900 transition-colors shrink-0"
              >
                + Add
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
